<template>
  <Modal
    title="Accounting Key"
    class-name="small-modal"
    :saving="saving"
    @submit="submit"
    @hide-modal="$emit('hide-modal')"
  >
    <div class="section-spacing-20">
      <TextField
        v-model="formData.accountingKey"
        class="full-width"
        name="Accounting Key"
        label="*Accounting Key:"
        :error-message="firstErrorMessage(v$.accountingKey)"
        data-cy="accountingKeyInput"
        :success="validKey"
      />
      <div v-if="duplicateCustomers.length" class="section-spacing-20">
        <div class="text-title">Other Accounts sharing this Key:</div>
        <ul class="section-spacing-20 text-body" data-cy="duplicateUsersList">
          <li v-for="user in duplicateCustomers" :key="user.id">
            <div>
              <a
                :href="userShowPath(user.customer_id)"
                target="_blank"
                class="font-bold"
                >{{ user.full_name }}
                <i class="fa-regular fa-arrow-up-right-from-square ml-1"></i>
              </a>
            </div>
            <div>
              {{ fullAddress(user.primary_address) }}
            </div>
          </li>
        </ul>
      </div>
      <TextArea
        v-model="formData.reason"
        name="Reason"
        label="*Reason:"
        :error-message="firstErrorMessage(v$.reason)"
        data-cy="reasonInput"
      />
      <div>
        <div class="d-flex align-items-start">
          <Checkbox
            v-model="formData.acknowledged"
            :class="['mr-2']"
            data-cy="acknowledgedCheckbox"
            :error="Boolean(firstErrorMessage(v$.acknowledged))"
          />
          <div>
            <div>
              I Acknowledge that the Accounting Key is used to sync records
              between Sera and your accounting platform. Changing this key can
              cause the mapping to break, if not updated properly.
              <a
                href="https://help.sera.tech/hc/en-us/articles/15804907395735-Accounting-Key"
                target="_blank"
                >Need help?</a
              >
            </div>
            <div class="error-text">
              {{ firstErrorMessage(v$.acknowledged) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import debounce from "lodash/debounce";
import { computed, onMounted, Ref, ref, watch } from "vue";
import { useStore } from "vuex";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import Checkbox from "@/components/Checkbox.vue";
import Modal from "@/components/Modal.vue";
import TextArea from "@/components/TextArea.vue";
import TextField from "@/components/TextField.vue";
import { userShowPath } from "@/mixins/path_helpers";
import { Customer } from "@/typings/customer";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required, sameAs } from "@vuelidate/validators";

const store = useStore();
const emit = defineEmits(["hide-modal", "save-success"]);
const props = defineProps<{ customer: Customer }>();
const _customer = computed(() => props.customer);
const formData = ref({
  accountingKey: _customer.value.accounting_key,
  reason: "",
  acknowledged: false
});
const saving = ref(false);
const validKey = ref(false);
const duplicateCustomers: Ref<Customer[]> = ref([]);
const v$ = useVuelidate(
  {
    accountingKey: { required },
    reason: { required },
    acknowledged: {
      required,
      truthy: helpers.withMessage("This is a required field", sameAs(true))
    }
  },
  formData.value
);

async function submit() {
  const valid = await v$.value.$validate();
  if (!valid) return;
  saving.value = true;
  const { accountingKey, reason } = formData.value;
  const payload = {
    accounting_key: accountingKey,
    accounting_key_change_reason: reason
  };
  try {
    await store.dispatch("setAccountingKey", {
      link: props.customer.links?.set_accounting_key,
      data: payload
    });
    window.notify.success("Accounting key updated successfully!");
    emit("save-success");
    emit("hide-modal");
  } catch (e) {
    console.error(e);
  }
  saving.value = false;
}

const validateAccountingKey = debounce(async function () {
  try {
    const res = await store.dispatch("validateAccountingKey", {
      link: props.customer.links?.fetch_shared_accounting,
      params: { accounting_key: formData.value.accountingKey }
    });
    // API returns all users with the key, including the current user
    // filter out current to check for any OTHER users
    const customers: Customer[] = res.data?.filter(
      (customer) => customer.customer_id !== props.customer.id
    );
    duplicateCustomers.value = customers;
    validKey.value = duplicateCustomers.value.length === 0;
  } catch (e) {
    console.error(e);
  }
}, 500);

onMounted(() => validateAccountingKey());

watch(
  () => formData.value.accountingKey,
  (newValue, oldValue) => {
    if (newValue !== oldValue && newValue) {
      validateAccountingKey();
    }
  }
);
</script>

<style scoped lang="scss"></style>
